import { useState } from 'react';
import { FiMail, FiShield, FiCheck, FiSend } from 'react-icons/fi';
import { useToast } from '@/hooks/use-toast';
import Loader from 'wrapper/loader';
import apiClient from '../../services/apiClient';
import { company_logo } from '../../constants/constant';
import styles from '@/styles/features/platform/platform-login.module.css';

const PlatformLogin = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting || loading) {
      console.log('Submission already in progress, skipping...');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Call the platform magic link endpoint
      const response = await apiClient.post('/platform/auth/magic-link', {
        email: formData.email
      });

      if (response.data.success) {
        setEmailSent(true);
        toast({ title: 'Magic link sent! Check your email to continue.' });
      }
    } catch (error) {
      console.error('Magic link error:', error);

      // Show specific error message from backend
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to send magic link. Please verify your email address.';
      toast({ title: errorMessage, variant: "destructive" });

      // Reset email sent state on error
      setEmailSent(false);
    } finally {
      setLoading(false);
      // Reset submitting flag after a delay to prevent rapid re-submissions
      setTimeout(() => {
        setIsSubmitting(false);
      }, 2000);
    }
  };

  const handleResend = () => {
    setEmailSent(false);
    // Clear the form to allow re-entry
    setFormData({ email: '' });
  };

  return (
    <div className={`bg-cover bg-center h-screen flex justify-center items-center ${styles.pageWrapper}`}>
      <div className="w-[420px] tertiary-bg rounded-xl flex flex-col items-center shadow-2xl">
        {/* Logo and Title */}
        <img
          src={company_logo || '/Logo.png'}
          alt="Company Logo"
          className="h-12 mt-8 mb-4"
        />
        <h3 className={`text-xl text-secondary tracking-tight mb-2 ${styles.title}`}>
          Secure Administrator Access
        </h3>
        <p className={`text-sm text-neutral600 mb-8 ${styles.subtitle}`}>
          MIRA Platform Management Portal
        </p>

        {!emailSent ? (
          // Email Form
          <form onSubmit={handleSubmit} className="w-full px-8 space-y-5">
            {/* Email Input */}
            <div>
              <label htmlFor="platform-admin-email" className={`block text-xs text-neutral700 mb-2 tracking-wide uppercase ${styles.emailLabel}`}>
                Administrator Email
              </label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primary opacity-60 z-10" size={18} />
                <input
                  id="platform-admin-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 border-neutral200 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary100 transition-all duration-200 text-base ${styles.emailInput}`}
                  placeholder="your.email@miralabs.ai"
                  required
                  autoComplete="email"
                />
              </div>
              <p className={`text-xs text-neutral500 mt-2 ${styles.emailHelpText}`}>
                Enter your authorized platform administrator email address
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-primary text-white py-4 rounded-lg hover:bg-primary600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 mt-6 shadow-lg hover:shadow-xl ${styles.submitButton}`}
            >
              {loading ? (
                <>
                  <Loader variant="button" />
                  <span>Sending Magic Link...</span>
                </>
              ) : (
                <>
                  <FiSend size={16} />
                  <span>Send Magic Link</span>
                </>
              )}
            </button>
          </form>
        ) : (
          // Email Sent Confirmation
          <div className="w-full px-8 py-4">
            <div className="bg-success_light border border-success200 rounded-lg p-6 text-center shadow-sm">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-success200 rounded-full flex items-center justify-center">
                  <FiCheck className="text-success600" size={24} />
                </div>
              </div>
              <h4 className={`text-lg text-neutral800 mb-2 ${styles.sentTitle}`}>
                Check Your Email
              </h4>
              <p className={`text-sm text-neutral600 mb-1 ${styles.sentBodyText}`}>
                We&apos;ve sent a secure magic link to:
              </p>
              <p className={`text-sm text-primary mb-4 ${styles.sentEmailText}`}>
                {formData.email}
              </p>
              <p className={`text-xs text-neutral500 mb-6 ${styles.sentHintText}`}>
                Click the link in your email to access the platform dashboard.
                The link expires in 1 hour.
              </p>

              <button
                onClick={handleResend}
                className={`text-sm text-primary hover:text-primary600 transition-colors duration-200 ${styles.tryAgainButton}`}
              >
                Didn&apos;t receive it? Try again
              </button>
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="mt-6 mb-4 px-8 w-full">
          <div className="bg-warning_light border border-warning200 rounded-lg px-4 py-3 shadow-sm">
            <p className={`text-xs text-neutral700 text-center flex items-center justify-center gap-2 ${styles.securityText}`}>
              <FiShield className="text-warning300" size={12} />
              <span className={styles.securityPrefix}>High Security Zone:</span>
              <span className={styles.securitySuffix}>Passwordless authentication only</span>
            </p>
          </div>
        </div>

        {/* Footer Link */}
        <div className="mb-4 text-center">
          <a
            href="/"
            className={`text-xs text-primary hover:text-primary600 transition-colors duration-200 ${styles.footerLink}`}
          >
            &larr; Return to Application Portal
          </a>
        </div>
      </div>
    </div>
  );
};

export default PlatformLogin;
